import React, {
  HTMLProps,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { Routes } from 'out-game/constants'
import { logout } from 'out-game/utils'
import * as chatActions from 'out-game/actions/ui/chat'
import {
  Badge,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
} from 'out-game/components'
import * as dataActions from 'out-game/actions/data'
import logoFullImage from 'out-game/images/logo full beta.png'
import { useAction, useTranslationStrict } from 'out-game/hooks'
import { ReactComponent as IconMenu } from 'out-game/images/icons/menu-icon.svg'
import { ReactComponent as Heart } from 'out-game/images/icons/heart-icon.svg'
import { ReactComponent as Wallet } from 'out-game/images/icons/wallet-icon.svg'
import { ReactComponent as Account } from 'out-game/images/icons/account-icon.svg'
import { ReactComponent as Speedometer } from 'out-game/images/icons/speedometer-icon.svg'
import { ReactComponent as Sword } from 'out-game/images/icons/sword-icon.svg'
import { ReactComponent as Handshake } from 'out-game/images/icons/handshake-icon.svg'
import { ReactComponent as Chat2 } from 'out-game/images/icons/chat2-icon.svg'
import { ReactComponent as PaperAndPencil } from 'out-game/images/icons/paper-and-pencil-icon.svg'
import { ReactComponent as Peoples } from 'out-game/images/icons/peoples-icon.svg'
import { ReactComponent as Gear } from 'out-game/images/icons/gear-icon.svg'
import { ReactComponent as Exit } from 'out-game/images/icons/exit-icon.svg'
import { ReactComponent as ArrowRight } from 'out-game/images/icons/arrow-right-icon.svg'
import { Merch } from 'out-game/types'

import * as actionsMain from 'out-game/actions/ui/main'
import * as actionsUI from 'out-game/actions/ui'
import {
  useRequestAddFavorite,
  useRequestGetFavorites,
} from '../hooks/useRequest'
import LanguageMenu from './LanguageMenu'
import { currencyContext } from './CurrencyContext'
import { CurrencySymbol } from 'out-game/root/types'
import { convertToCurrency } from 'out-game/utils/currency'

const useStyles = makeStyles({
  root: {
    '& .og-link': {
      textDecoration: 'none',
    },
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  enter: {
    '&:hover > p': {
      color: 'white',
    },
  },
  iconWithText: {
    display: 'flex',
    '& *:not(:last-child)': {
      marginRight: 5,
    },
    '& p': {
      fontSize: '22px',
    },
  },
  closeNotifications: {
    '& svg': {
      width: 15,
    },
    width: 19,
  },
  user: {
    minWidth: 112,
    justifyContent: 'flex-end',
  },
  userMenuIcon: {
    width: 33,
    display: 'flex',
    justifyContent: 'center',
    marginRight: 23,
  },
  listDivider: {
    marginLeft: 15,
    marginRight: 15,
    marginTop: 7,
    marginBottom: 7,
    height: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  notificationCounter: {
    cursor: 'pointer',
  },
  notificationsMenu: {
    margin: 0,
  },
  menu: {
    '& .og-dropdown_header-container': {
      cursor: 'pointer',
      padding: 2,
      paddingLeft: 10,
    },
    '&:hover': {
      '& > .og-dropdown_header-container': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 10,
      },
    },
  },
})

const IconNotificationPanel: React.FC = () => {
  const classes = useStyles()
  const notifications = useSelector((state) => state.data.notifications)

  const setNotificationPanelVisible = useAction(
    actionsUI.setNotificationPanelVisible
  )

  if (!notifications.length) {
    return (
      <IconButton
        className={classes.notificationsMenu}
        Icon={IconMenu}
        onClick={() => setNotificationPanelVisible(true)}
      />
    )
  }
  return (
    <Badge
      className={classes.notificationCounter}
      color="Primary"
      width={28}
      height={24}
      content={notifications.length > 99 ? '!' : notifications.length}
      onClick={() => setNotificationPanelVisible(true)}
    />
  )
}

type Props = HTMLProps<HTMLDivElement>

const AppBar: React.FC<Props> = (props) => {
  const { t } = useTranslationStrict('menu')

  const { currency } = useContext(currencyContext)
  
  const classes = useStyles()
  const history = useHistory()
  const user = useSelector((state) => state.data.user)
  const auth = useSelector((state) => state.data.authenticated)
  const favorites = useSelector((state) => state.data.favorites)

  const [favoritesCount, setFavoritesCount] = useState<number | null>(null)

  const resetMainFilters = useAction(actionsMain.reset)
  const { className } = props
  const unreadMessagesCount = useSelector(
    (state) => state.ui.chat.unreadMessagesCount
  )
  const setIsChatReloadRequired = useAction(chatActions.setIsChatReloadRequired)
  const setFavorites = useAction(dataActions.setFavoriteList)
  const setIsMessagesReloadRequired = useAction(
    chatActions.setIsMessagesReloadRequired
  )

  const [addFavorite] = useRequestAddFavorite()
  const [getFavorites] = useRequestGetFavorites()

  const getFavoriteList = useCallback(() => {
    if (!user) return

    getFavorites({})
      .then(({ data }) => setFavorites(data))
      .catch(console.error)
  }, [getFavorites, user])

  const addMerchToFavorites = useCallback(
    (item_id: number) => {
      addFavorite({ item_id })
        .then((response) => {
          if (!response?.error) getFavoriteList()
          else throw Error(response.error)
        })
        .catch(console.error)
    },
    [addFavorite, getFavoriteList]
  )

  useEffect(() => {
    if (!user) return

    getFavorites({})
      .then((response) => {
        const localFavorites: Merch[] = JSON.parse(localStorage.getItem('favorites') || '')
        const favoritesResponse = response.data

        const newFavorites = localFavorites.filter(({ id }) => {
          return !favoritesResponse.find(({ id: item_id }) => item_id === id)
        })

        if (newFavorites?.length === 0) {
          setFavorites(favoritesResponse)
        } else {
          newFavorites.forEach(({ id }) => addMerchToFavorites(id))
        }
      })
      .catch(console.error)
  }, [getFavorites, user])

  useEffect(() => {
    setFavoritesCount(favorites.length)
  }, [favorites])

  return (
    <header className={cn(classes.root, className, 'header')}>
      <nav className={cn(classes.nav, 'header__nav')}>
        <RouterLink to={Routes.Root} onClick={() => resetMainFilters()}>
          <img alt="logo" src={logoFullImage} height={68} width={179} className="header__logo" />
        </RouterLink>

        <Menu
          className={classes.menu}
          Header={
            <Link color="Emphasized" to={Routes.AboutUs} size={22}>
              {t('aboutUs')}
            </Link>
          }
        >
          <MenuItem onClick={() => history.push(Routes.WhoWeAre)} button>
            {t('whoWeAre')}
          </MenuItem>
          <MenuItem onClick={() => history.push(Routes.Contacts)} button>
            {t('contacts')}
          </MenuItem>
        </Menu>

        <Menu
          className={classes.menu}
          Header={
            <Link color="Emphasized" to={Routes.HowItWorks} size={22}>
              {t('howItWorks')}
            </Link>
          }
        >
          <MenuItem
            onClick={() => history.push(Routes.HowItWorksSubscription)}
            button
          >
            {t('rates')}
          </MenuItem>
          <MenuItem onClick={() => history.push(Routes.HowItWorksBuy)} button>
            {t('purchase')}
          </MenuItem>
          <MenuItem onClick={() => history.push(Routes.HowItWorksSell)} button>
            {t('selling')}
          </MenuItem>
          <MenuItem
            onClick={() => history.push(Routes.HowItWorksService)}
            button
          >
            {t('services')}
          </MenuItem>
          <MenuItem
            onClick={() => history.push(Routes.HowItWorksTransactions)}
            button
          >
            {t('paymentsAndRefunds')}
          </MenuItem>
          <MenuItem
            onClick={() => history.push(Routes.HowItWorksSanctions)}
            button
          >
            {t('rulesAndSanctions')}
          </MenuItem>
          <MenuItem
            onClick={() => history.push(Routes.HowItWorksResponsibility)}
            button
          >
            {t('sellerLiability')}
          </MenuItem>
        </Menu>

        <Menu
          className={classes.menu}
          Header={
            <Typography color="Emphasized" size={22}>
              {t('help')}
            </Typography>
          }
        >
          <MenuItem onClick={() => history.push(Routes.Faq)} button>
            FAQ
          </MenuItem>
          <MenuItem onClick={() => history.push(Routes.Helpdesk)} button>
            {t('contactSupport')}
          </MenuItem>
        </Menu>

        <LanguageMenu className={classes.menu} />

        <IconButton
          style={{ display: 'flex' }}
          Icon={Heart}
          onClick={() => history.push(Routes.Favorite)}
          className={cn(classes.iconWithText)}
        >
          <Typography color="Primary" size={16} weight="bold">
            {favoritesCount || ''}
          </Typography>
        </IconButton>

        {user && (
          <IconButton
            style={{ display: 'flex' }}
            Icon={Wallet}
            onClick={() => history.push(Routes.UserWallet)}
            className={cn(classes.iconWithText, 'header__wallet')}
          >
            <Typography color="Primary" size={16} weight="bold">
              {convertToCurrency(user.balance_user[0].balance, currency)} {CurrencySymbol[currency]}
            </Typography>
          </IconButton>
        )}

        {user && (
          <>
            <Menu
              className={cn(classes.menu, classes.user)}
              Header={
                <Link to={Routes.Profile + '/' + user.nickname}>
                  <IconButton
                    Icon={Account}
                    className={classes.iconWithText}
                  >
                    <Typography color="Primary" size={22}>
                      {user.nickname}
                    </Typography>
                  </IconButton>
                </Link>
              }
            >
              <MenuItem
                onClick={() => history.push(Routes.UserDashboard)}
                button
              >
                <span className={classes.userMenuIcon}>
                  <Speedometer />
                </span>
                Dashboard
              </MenuItem>
              <MenuItem
                onClick={() => history.push(Routes.UserInventory)}
                button
              >
                <span className={classes.userMenuIcon}>
                  <Sword />
                </span>
                {t('inventory')}
              </MenuItem>
              <MenuItem onClick={() => history.push(Routes.UserDeals)} button>
                <span className={classes.userMenuIcon}>
                  <Handshake />
                </span>
                {t('myDeals')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setIsChatReloadRequired(true)
                  setIsMessagesReloadRequired(true)
                  history.push(Routes.UserChat)
                }}
                button
              >
                <span className={classes.userMenuIcon}>
                  <Chat2 />
                </span>
                {t('dealChat')}
                {unreadMessagesCount > 0 && <span>!</span>}
              </MenuItem>
              <MenuItem
                onClick={() => history.push(Routes.Subscription)}
                button
              >
                <span className={classes.userMenuIcon}>
                  <PaperAndPencil />
                </span>
                {t('subscriptions')}
              </MenuItem>
              <MenuItem
                onClick={() => history.push(Routes.UserPartners)}
                button
              >
                <span className={classes.userMenuIcon}>
                  <Peoples />
                </span>
                {t('affiliate')}
              </MenuItem>
              <MenuItem onClick={() => history.push(Routes.UserWallet)} button>
                <span className={classes.userMenuIcon}>
                  <Wallet />
                </span>
                {t('wallet')}
              </MenuItem>
              <MenuItem
                onClick={() => history.push(Routes.UserSettings)}
                button
              >
                <span className={classes.userMenuIcon}>
                  <Gear />
                </span>
                {t('settings')}
              </MenuItem>
              <div className={classes.listDivider} />
              <MenuItem onClick={() => logout()} button>
                <span className={classes.userMenuIcon}>
                  <Exit />
                </span>
                {t('logOut')}
              </MenuItem>
            </Menu>
            <IconNotificationPanel />
          </>
        )}
        {auth === null && <Skeleton variant="rect" width={100} />}
        {auth === false && (
          <IconButton
            Icon={ArrowRight}
            className={cn(classes.iconWithText, classes.enter)}
            onClick={() => history.push(Routes.AccessLogin)}
            childPosition="left"
          >
            <Typography size={18}>{t('logIn')}</Typography>
          </IconButton>
        )}
      </nav>
    </header>
  )
}

export default AppBar
